import { useState, useEffect, type CSSProperties } from 'react';
import Icon from '../ui/Icon';
import { S } from '../../config/theme.js';

/**
 * Shape des Banner-Inhalts — wird als Prop in `LiveBanner` übergeben.
 * Alle Felder optional außer `title` und `sub`, damit der Caller minimal
 * aufrufen kann.
 */
export interface LiveBannerData {
  title: string;
  sub: string;
  /** Icon-Name (Default: `'email'`). */
  icon?: string;
  /** Button-Label rechts im Banner. Wird nicht angezeigt wenn leer. */
  btnLabel?: string;
  /** Hintergrund-Gradient/Farbe. Default: dunkelgrüner Gradient. */
  bg?: string;
  /** Border-Farbe. Default: transparentes Hellgrün. */
  borderColor?: string;
}

interface LiveBannerProps {
  /** Banner-Inhalt — wenn `null`/`undefined`, wird nichts gerendert. */
  banner: LiveBannerData | null | undefined;
  /** Callback wenn Banner geschlossen wird (Timer, Klick, oder X-Button). */
  onClose: () => void;
  /** Callback bei Klick auf den Banner-Body (optional). */
  onAction?: () => void;
}

/**
 * LiveBanner — Animiertes In-App-Benachrichtigungs-Banner (auto-dismiss nach 6s).
 *
 * Ein Klick auf den Banner-Body triggert `onAction` und schließt den Banner.
 * Das X rechts schließt ohne Action auszulösen.
 */
export function LiveBanner({ banner, onClose, onAction }: LiveBannerProps) {
  const [exiting, setExiting] = useState<boolean>(false);

  useEffect(function () {
    const timer = setTimeout(function () {
      setExiting(true);
      setTimeout(onClose, 300);
    }, 6000);
    return function () {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!banner) return null;

  const dismiss = function () {
    setExiting(true);
    setTimeout(onClose, 300);
  };

  return (
    <div
      className={'live-banner' + (exiting ? ' live-banner-exit' : '')}
      onClick={function () {
        if (onAction) onAction();
        dismiss();
      }}
      style={{
        background: banner.bg || 'linear-gradient(135deg,#1f2e22,#2d4a32)',
        color: '#fff',
        border: '1px solid ' + (banner.borderColor || 'rgba(74,222,128,.3)'),
      }}>
      <div style={{ ...(S.row as CSSProperties), flexShrink: 0 }}>
        <Icon name={banner.icon || 'email'} size={24} color="#fff" />
      </div>
      <div className="flex-1">
        <div
          style={{ ...(S.labelLg as CSSProperties), fontWeight: 700, marginBottom: 2 }}>
          {banner.title}
        </div>
        <div style={{ fontSize: 11, opacity: 0.8 }}>
          {banner.sub}
        </div>
      </div>
      {banner.btnLabel &&
        <div
          style={{
            ...(S.label as CSSProperties),
            background: 'rgba(255,255,255,.2)',
            borderRadius: 10,
            padding: '6px 14px',
            flexShrink: 0,
            whiteSpace: 'nowrap' as const,
          }}>
          {banner.btnLabel}
        </div>}
      <button
        onClick={function (e) {
          e.stopPropagation();
          dismiss();
        }}
        style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,.5)',
          fontSize: 16,
          cursor: 'pointer',
          padding: '2px 6px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}>
        <Icon name="cross" size={14} color="rgba(255,255,255,.5)" />
      </button>
    </div>
  );
}
